import { css } from '@emotion/react';
import { Box, Text } from '@inflearn/ds-react-next';
import type { ParseKeys } from 'i18next';
import { useTranslation } from 'next-i18next';
import type { ReactNode } from 'react';

import { SectionHead } from './SectionHead';
import { LANDING_COLOR, LANDING_FONT, LANDING_MOBILE_MQ, LANDING_SPACE } from './constants';

const PANEL_HEIGHT = 168;

/** STEP 2 — 1분 만에 시작. 카드마다 실제 화면을 축약한 패널을 붙인다 */
export const QuickStart = () => {
  const { t } = useTranslation();

  const cards: Array<{
    key: string;
    bg: string;
    titleColor: string;
    titleKey: ParseKeys;
    descKey: ParseKeys;
    panel: ReactNode;
  }> = [
    {
      key: 'no_resume',
      bg: '#E5F0FF',
      titleColor: '#1C4FB8',
      titleKey: 'ai_interviews.landing.quick.no_resume.title',
      descKey: 'ai_interviews.landing.quick.no_resume.desc',
      panel: (
        <>
          <Text css={styles.panelHeading}>
            {t('ai_interviews.landing.quick.no_resume.panel_title')}
          </Text>
          <Box css={styles.panelRow}>
            <span>{t('ai_interviews.landing.quick.no_resume.panel_resume')}</span>
            <em css={styles.panelTag}>
              {t('ai_interviews.landing.quick.no_resume.panel_optional')}
            </em>
          </Box>
          <Text css={styles.panelDrop}>
            {t('ai_interviews.landing.quick.no_resume.panel_drop')}
          </Text>
          <Text component="span" css={styles.panelButton}>
            {t('ai_interviews.landing.quick.no_resume.panel_button')}
          </Text>
        </>
      ),
    },
    {
      key: 'resume',
      bg: '#EFEAFF',
      titleColor: '#5B3CC4',
      titleKey: 'ai_interviews.landing.quick.resume.title',
      descKey: 'ai_interviews.landing.quick.resume.desc',
      panel: (
        <>
          <Box css={styles.panelFile}>
            <i css={styles.panelFileBadge}>PDF</i>
            <span css={styles.panelFileName}>
              {t('ai_interviews.landing.quick.resume.panel_file')}
            </span>
            <em css={styles.panelTag}>{t('ai_interviews.landing.quick.resume.panel_done')}</em>
          </Box>
          {(['panel_q1', 'panel_q2'] as const).map((q) => (
            <Box key={q} css={styles.panelQuestion}>
              <small css={styles.panelQuestionTag}>
                {t('ai_interviews.landing.quick.resume.panel_based')}
              </small>
              {t(`ai_interviews.landing.quick.resume.${q}`)}
            </Box>
          ))}
        </>
      ),
    },
    {
      key: 'resume_later',
      bg: '#FFEEE2',
      titleColor: '#C2541B',
      titleKey: 'ai_interviews.landing.quick.continue.title',
      descKey: 'ai_interviews.landing.quick.continue.desc',
      panel: (
        <>
          <Text css={styles.panelHeading}>
            {t('ai_interviews.landing.quick.continue.panel_title')}
          </Text>
          <Box css={styles.panelRow}>
            <span>{t('ai_interviews.landing.quick.continue.panel_meta')}</span>
            <em css={[styles.panelTag, styles.panelTagMint]}>
              {t('ai_interviews.landing.quick.continue.panel_step')}
            </em>
          </Box>
          <Box css={styles.panelBar}>
            <i css={styles.panelBarFill} />
          </Box>
          <Text component="span" css={styles.panelButton}>
            {t('ai_interviews.landing.quick.continue.panel_button')}
          </Text>
        </>
      ),
    },
  ];

  return (
    <Box component="section" css={styles.section} data-reveal aria-labelledby="ai-quick-title">
      <Box css={styles.inner}>
        <SectionHead id="ai-quick-title" step={2} titleKey="ai_interviews.landing.quick.title" />

        <Box component="ul" css={styles.cards}>
          {cards.map((card) => (
            <Box key={card.key} component="li" css={styles.card} style={{ background: card.bg }}>
              <Text component="h3" css={styles.cardTitle} style={{ color: card.titleColor }}>
                {t(card.titleKey)}
              </Text>
              <Text css={styles.cardDesc}>{t(card.descKey)}</Text>
              <Box css={styles.panel} aria-hidden>
                {card.panel}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const styles = {
  section: css({
    background: '#fff',
    padding: `${LANDING_SPACE.sectionY} ${LANDING_SPACE.pageX}`,
  }),
  inner: css({
    maxWidth: 1200,
    margin: '0 auto',
  }),
  cards: css({
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 20,
    margin: `${LANDING_SPACE.blockTop} 0 0`,
    padding: 0,
    listStyle: 'none',
    [LANDING_MOBILE_MQ]: {
      gridTemplateColumns: '1fr',
      gap: 12,
    },
  }),
  card: css({
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    padding: '44px 40px 0',
    borderRadius: 24,
    [LANDING_MOBILE_MQ]: {
      padding: '28px 22px 0',
    },
  }),
  cardTitle: css({
    margin: 0,
    fontSize: LANDING_FONT.sectionTitleSm,
    fontWeight: 800,
    lineHeight: 1.35,
    letterSpacing: '-0.02em',
    wordBreak: 'keep-all',
  }),
  cardDesc: css({
    margin: '10px 0 40px',
    color: '#3D434A',
    fontSize: LANDING_FONT.introBody,
    fontWeight: 500,
    lineHeight: 1.55,
    letterSpacing: '-0.02em',
    wordBreak: 'keep-all',
    [LANDING_MOBILE_MQ]: {
      marginBottom: 24,
    },
  }),
  panel: css({
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    height: PANEL_HEIGHT,
    margin: 'auto 28px 0',
    padding: '14px 14px 16px',
    overflow: 'hidden',
    borderRadius: '16px 16px 0 0',
    background: '#fff',
    boxShadow: '0 -6px 24px -14px rgba(17,19,21,.3)',
    color: LANDING_COLOR.ink,
    [LANDING_MOBILE_MQ]: {
      margin: '0 12px',
    },
  }),
  panelHeading: css({
    fontSize: 12,
    fontWeight: 800,
  }),
  panelRow: css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: 12,
    fontWeight: 600,
  }),
  panelTag: css({
    flexShrink: 0,
    padding: '2px 6px',
    borderRadius: 6,
    background: LANDING_COLOR.surface,
    color: LANDING_COLOR.subtle,
    fontSize: 11,
    fontStyle: 'normal',
    fontWeight: 700,
  }),
  panelTagMint: css({
    background: '#E6FCF5',
    color: LANDING_COLOR.mintDeep,
  }),
  panelDrop: css({
    padding: 9,
    borderRadius: 10,
    border: '1px dashed #D5DAE0',
    color: LANDING_COLOR.subtle,
    fontSize: 11,
    textAlign: 'center',
  }),
  panelButton: css({
    display: 'block',
    marginTop: 1,
    padding: 8,
    borderRadius: 8,
    background: LANDING_COLOR.ink,
    color: '#fff',
    fontSize: 12,
    fontWeight: 700,
    textAlign: 'center',
  }),
  panelFile: css({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 10px',
    borderRadius: 10,
    border: '1px solid #E6E9EC',
    fontSize: 12,
    fontWeight: 600,
  }),
  panelFileBadge: css({
    padding: '4px 6px',
    borderRadius: 6,
    background: '#FFE3E3',
    color: '#E03131',
    fontSize: 10,
    fontStyle: 'normal',
    fontWeight: 800,
  }),
  panelFileName: css({
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }),
  panelQuestion: css({
    padding: '8px 10px',
    borderRadius: 10,
    background: LANDING_COLOR.surface,
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.45,
  }),
  panelQuestionTag: css({
    display: 'block',
    marginBottom: 4,
    color: LANDING_COLOR.mintDeep,
    fontSize: 10,
    fontWeight: 800,
  }),
  panelBar: css({
    height: 6,
    borderRadius: 999,
    background: '#E9ECEF',
    overflow: 'hidden',
  }),
  panelBarFill: css({
    display: 'block',
    width: '60%',
    height: '100%',
    background: LANDING_COLOR.mint,
  }),
};
